import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Building2 } from 'lucide-react';

import { Button } from '../../../ui/button';
import { Spinner } from '../../../ui/spinner';
import { AuthFrame } from '../../routes/AuthFrame';
import type { RouteModule } from '../../routes/contract';
import { AUTH_ROUTE_PATHS } from '../../routes/paths';
import { useAuthSession } from '../../auth/AuthProvider';
import { tt } from '../i18n';
import { InlineCredential } from './InlineCredential';
import { InvitationBlocked } from './InvitationBlocked';
import { InvitationSummary } from './InvitationSummary';
import { MyInvitations } from './MyInvitations';
import { ta } from './messages';
import { SwitchAccountBanner } from './SwitchAccountBanner';
import { toBlockedState, type BlockedState } from './schemas';
import { takeTokenFromLocation } from './token';
import { useAcceptInvitation } from './useAcceptInvitation';
import { useDeclineInvitation } from './useDeclineInvitation';
import { useInvitationPreview } from './useInvitationPreview';
import { VerifyAddress } from './VerifyAddress';

export function AcceptInvitation(): ReactNode {
  // Read once, at mount, and held here for the rest of the visit: `token.ts`
  // rewrites the address bar on that first read, so a second call would find
  // nothing. This state is also the reason nothing on this screen navigates.
  const [token] = useState(() => takeTokenFromLocation(window.location, window.history));
  const { session, state } = useAuthSession();
  const navigate = useNavigate();
  const acceptance = useAcceptInvitation();
  const decline = useDeclineInvitation();

  const email = state.user?.email ?? null;
  const isAuthed = state.status === 'signed-in' && email !== null;
  const preview = useInvitationPreview(token, isAuthed);

  // A token carries the sign-in ladder below (`InlineCredential`, so the
  // bearer credential is not lost to a navigation). With no token there is
  // nothing to preserve, so a signed-out visitor goes through the ordinary
  // sign-in route instead of a second inline form.
  useEffect(() => {
    if (token === null && state.status === 'signed-out') {
      void navigate({ to: AUTH_ROUTE_PATHS.signIn });
    }
  }, [token, state.status, navigate]);

  if (token === null) {
    if (state.status !== 'signed-in') return null;
    return (
      <AuthFrame title={tt('accept.page_title')} description={tt('accept.page_subtitle')}>
        {state.user?.emailVerified === true ? <MyInvitations /> : <VerifyAddress />}
      </AuthFrame>
    );
  }

  const blockedState: BlockedState | null =
    preview.data?.kind === 'unavailable'
      ? preview.data.state
      : acceptance.data && acceptance.data.status !== 'accepted'
        ? toBlockedState(acceptance.data.status)
        : decline.data?.kind === 'unavailable'
          ? decline.data.state
          : null;

  const isMutating = acceptance.isPending || decline.isPending;

  const handleAccept = (): void => {
    if (token !== null) acceptance.mutate({ token });
  };

  const handleDecline = (): void => {
    if (token !== null) decline.mutate({ token });
  };

  return (
    <AuthFrame
      title={tt('accept.invite.page_title')}
      align="center"
      icon={<Building2 aria-hidden className="size-6 text-foreground" />}
    >
      {decline.isError && (
        <p role="alert" className="text-body-sm text-destructive">
          {ta('declineFailed')}
        </p>
      )}
      {state.status === 'signed-in' && (
        <SwitchAccountBanner email={email} onSwitch={() => void session.signOut()} />
      )}
      {state.status === 'loading' ? null : state.status === 'signed-out' ? (
        <InlineCredential />
      ) : email === null ? null : blockedState !== null ? (
        <InvitationBlocked
          state={blockedState}
          sender={preview.data?.kind === 'available' ? preview.data.invited_by_email : null}
          onDecline={handleDecline}
          declining={isMutating}
        />
      ) : preview.isPending ? (
        <div
          role="status"
          className="flex items-center justify-center gap-2 text-body-sm text-muted-foreground"
        >
          <Spinner aria-hidden className="size-4" />
          <span>{ta('loading')}</span>
        </div>
      ) : acceptance.isError || (preview.isError && preview.error.failure.kind === 'not-found') ? (
        // One sentence for every cause the server does not distinguish
        // (cancelled, expired, already used, unknown, wrong address) and for
        // a 200 body this client could not parse -- a malformed accept
        // response means the membership was already created, so this is not
        // a case to explain further, only to let the reader ask again.
        <p role="alert" className="text-body-sm text-destructive">
          {tt('accept.refused.opaque')}
        </p>
      ) : preview.isError ? (
        <>
          <p role="alert" className="text-body-sm text-destructive">
            {ta('failed')}
          </p>
          <Button type="button" variant="outline" onClick={() => void preview.refetch()}>
            {ta('action.retry')}
          </Button>
        </>
      ) : preview.data?.kind === 'available' ? (
        <>
          <InvitationSummary
            tenantName={preview.data.tenant_name}
            campaignName={preview.data.campaign_name}
            role={preview.data.role}
            invitedByEmail={preview.data.invited_by_email}
          />
          <div className="flex flex-col gap-3 sm:flex-row">
            <Button type="button" className="flex-1" disabled={isMutating} onClick={handleAccept}>
              {tt('accept.invite.accept_action')}
            </Button>
            <Button type="button" variant="outline" disabled={isMutating} onClick={handleDecline}>
              {ta('action.decline')}
            </Button>
          </div>
        </>
      ) : null}
    </AuthFrame>
  );
}

/** Declared beside the component rather than in `../routes.ts`, so the route
 *  registration task consumes one value instead of restating the path and the
 *  (absent) capability. `requires` stays undefined on purpose: the invitee
 *  holds no membership yet, which is the whole point of the screen. */
export const ACCEPT_ROUTE: RouteModule = {
  path: '/convite',
  element: <AcceptInvitation />,
  kind: 'form',
};
